import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  IconButton, 
  Checkbox, 
  Drawer,
  Link,
  TextField
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Close as CloseIcon } from '@mui/icons-material';

const AadhaarOtpAuth = ({ onBack, onVerify, aadhaarNumber }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(180);
  const [consent, setConsent] = useState(false);
  const [isConsentOpen, setIsConsentOpen] = useState(false);
  const inputRefs = useRef([]);

  // Format Aadhaar Number to display as XXXX-XXXX-XXXX
  const formattedAadhaar = aadhaarNumber 
    ? aadhaarNumber.replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3')
    : 'XXXX-XXXX-XXXX';

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleOtpChange = (index, value) => {
    // allow only numbers
    if (value && !/^\d+$/.test(value)) return;
    
    // take only last char if multiple entered
    const val = value.slice(-1);
    
    const newOtp = [...otp];
    newOtp[index] = val;
    setOtp(newOtp);

    // auto focus next
    if (val && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // move back on backspace if empty
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newOtp = [...otp];
      pastedData.split('').forEach((char, i) => {
        if (i < 6) newOtp[i] = char;
      });
      setOtp(newOtp);
      // Focus on the next empty input or the last one
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleResend = () => {
    console.log("Resending OTP...");
    setTimer(180);
    setOtp(['', '', '', '', '', '']);
  };

  const handleVerify = () => {
    const otpValue = otp.join('');
    const payload = {
      aadhaarNumber,
      otp: otpValue,
      consent
    };
    console.log("OTP Auth Payload:", payload);
    if (onVerify) {
      onVerify(payload);
    }
  };

  const isOtpComplete = otp.every((digit) => digit !== '');
  const isFormValid = isOtpComplete && consent;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{bgcolor:'blue', mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography  sx={{ textTransform: 'uppercase' }}>Registered Number Authentication</Typography>
      </Box>

      <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* UID Number */}
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', '&::before': { content: '"*"', color: 'error.main', mr: 0.5 } }} fontWeight={500} gutterBottom>
            Aadhaar UID Number
          </Typography>
          <Typography variant="h6" fontWeight="bold">
            {formattedAadhaar}
          </Typography>
        </Box>

        {/* 6 Digit OTP */}
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} fontWeight={500} gutterBottom>
            6 Digit OTP
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 1 }} onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  variant="outlined"
                  size="small"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  inputProps={{
                    maxLength: 1,
                    style: { textAlign: 'center', width: '24px', padding: '10px 8px' }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
              ))}
            </Box>
            
            <Button 
              variant="outlined" 
              onClick={handleResend}
              disabled={timer > 0}
              sx={{ textTransform: 'none', px: 3, borderRadius: 1 }}
            >
              Resend
            </Button>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
            {timer > 0 ? `${timer} sec` : '0 sec'}
          </Typography>
        </Box>

        {/* Consent Checkbox */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1 }}>
          <Checkbox 
            checked={consent} 
            onChange={(e) => setConsent(e.target.checked)} 
            color="primary"
            sx={{ pt: 0, pl: 0 }}
          />
          <Typography variant="body2" color="text.primary">
            I hereby confirm that I have read out the information provided by the beneficiary...{' '}
            <Link 
              component="button" 
              variant="body2" 
              onClick={(e) => {
                e.preventDefault();
                setIsConsentOpen(true);
              }}
              sx={{ fontWeight: 'bold', textDecoration: 'none', cursor: 'pointer', verticalAlign: 'baseline' }}
            >
              Read More
            </Link>
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
        <Button 
          variant="contained" 
          onClick={handleVerify} 
          disabled={!isFormValid}
          sx={{ borderRadius: 3, px: 4, py: 1.5, textTransform: "none" }}
        >
          Verify Member
        </Button>
      </Box>

      {/* Aadhaar Consent Drawer/Bottom Sheet */}
      <Drawer
        anchor="bottom"
        open={isConsentOpen}
        onClose={() => setIsConsentOpen(false)}
        PaperProps={{
          sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, pb: 4, pt: 2, px: 2, maxHeight: '80vh' }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setIsConsentOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ px: 2, overflowY: 'auto' }}>
          <Typography variant="body1" paragraph>
            I, hereby declare that I am voluntarily sharing my Aadhaar Number / Virtual ID issued by UIDAI and demographic information, with National Health Authority (NHA) for the sole purpose of creation of an Ayushman Bharat Health Account (ABHA (number)).
          </Typography>
          <Typography variant="body1" paragraph>
            I understand that my ABHA (number) can be used and shared for purposes as may be notified by Ayushman Bharat Digital Mission (ABDM) from time to time including provision of healthcare services. Further, I am aware that my personal identifiable information (Name, Address, Age, Date of Birth, Gender and Photograph) may be made available to the entities working in the National Digital Health Ecosystem (NDHE) which inter alia may include stakeholders and entities such as healthcare professionals (e.g. doctors), facilities (e.g. hospitals, laboratories) and data fiduciaries (e.g. health programmes), which are registered with or linked to the Ayushman Bharat Digital Mission (ABDM), and various processes there under.
          </Typography>
          <Typography variant="body1">
            I authorize NHA to use my Aadhaar number / Virtual ID for performing Aadhaar based authentication with UIDAI as per the provisions of the Aadhaar (Targeted Delivery of Financial and other Subsidies, Benefits and Services) Act, 2016 for the aforesaid purpose. I understand that UIDAI will share my e-KYC details, or response of "Yes" with NHA upon successful authentication.
          </Typography>
        </Box>
      </Drawer>
    </Box>
  );
};

export default AadhaarOtpAuth;
